import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Checkbox,
  VStack,
  HStack,
  Text,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';

import type { AvailableCarrier } from 'api/quote-marketplace';

interface MarketOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableCarriers: AvailableCarrier[];
  selectedMarkets: string[];
  onApply: (selectedMarkets: string[]) => void;
  isLoading?: boolean;
}

const MarketOptionsModal = ({
  isOpen,
  onClose,
  availableCarriers,
  selectedMarkets,
  onApply,
  isLoading = false,
}: MarketOptionsModalProps) => {
  const [localSelection, setLocalSelection] = useState<string[]>([]);
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  useEffect(() => {
    if (isOpen) {
      setLocalSelection(
        selectedMarkets.length > 0
          ? selectedMarkets
          : availableCarriers.map((c) => c.product),
      );
    }
  }, [isOpen, selectedMarkets, availableCarriers]);

  const handleToggle = (product: string) => {
    setLocalSelection((prev) =>
      prev.includes(product)
        ? prev.filter((p) => p !== product)
        : [...prev, product],
    );
  };

  const handleSelectAll = () => {
    setLocalSelection(availableCarriers.map((c) => c.product));
  };

  const handleDeselectAll = () => {
    setLocalSelection([]);
  };

  const handleApply = () => {
    const allSelected =
      localSelection.length === availableCarriers.length ||
      localSelection.length === 0;
    onApply(allSelected ? [] : localSelection);
  };

  const allSelected =
    localSelection.length === availableCarriers.length ||
    availableCarriers.every((c) => localSelection.includes(c.product));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Market Options</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <HStack justify="space-between" mb={4}>
            <Text fontSize="sm" color="gray.600">
              Select carriers to include in quote request
            </Text>
            <Button
              size="xs"
              variant="link"
              colorScheme="yellow"
              onClick={allSelected ? handleDeselectAll : handleSelectAll}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            {availableCarriers.map((carrier) => (
              <Checkbox
                key={carrier.product}
                isChecked={localSelection.includes(carrier.product)}
                onChange={() => handleToggle(carrier.product)}
                borderColor={borderColor}
              >
                {carrier.display_name}
              </Checkbox>
            ))}
          </VStack>
          {localSelection.length === 0 && (
            <Text fontSize="sm" color="orange.500" mt={4}>
              At least one carrier must be selected
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="yellow"
            onClick={handleApply}
            isLoading={isLoading}
            isDisabled={localSelection.length === 0}
          >
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MarketOptionsModal;
